import {
  type IndicatorBandingConfig,
  indicatorBandingConfigSchema,
} from '@risksmart-app/domain/src/types/indicator-banding-config';
import { useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';
import { useTRPC } from 'src/utils/trpc';

export type { IndicatorBandingConfig };
export { indicatorBandingConfigSchema };

/**
 * Latest banding config for a single indicator. tRPC-only — unlike most
 * hooks in this directory, `indicator_banding_config` has no GraphQL/Hasura
 * equivalent to bridge from (it's a new, CUS-183 entity), so there is no
 * `createQueryHook` dual-path here. Deliberately NOT gated on the `trpc`
 * migration flag: banding rollout is controlled by `configurable_thresholds`
 * at the consuming surface, and must work for orgs still on the GraphQL
 * path for migrated operations.
 *
 * Not wired into any component in this PR — PR5/PR6 (CUS-187/CUS-188) consume it.
 */
export const useGetLatestIndicatorBandingConfig = (
  indicatorId: string | undefined,
  options?: { isSkipped?: boolean }
) => {
  const trpc = useTRPC();
  const enabled = !!indicatorId && !options?.isSkipped;

  const { data, isLoading, error, refetch } = useQuery({
    ...trpc.frontend.indicatorBandingConfig.latestByIndicatorId.queryOptions({
      indicatorId: indicatorId ?? '',
    }),
    enabled,
  });

  const record = data?.[0];
  const config = useMemo(
    () =>
      record?.Config
        ? indicatorBandingConfigSchema.parse(record.Config)
        : undefined,
    [record?.Config]
  );

  return {
    config,
    shape: record?.Shape,
    id: record?.Id,
    version: record?.Version,
    modifiedAt: record?.ModifiedAtTimestamp,
    isSeeded: record?.IsSeeded,
    loading: enabled && isLoading,
    error,
    refetch,
  };
};
